import Vue from 'vue';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

const LikeDislikePollModal = () => import('@/components/modals/LikeDislikePollModal.vue');

import { MarkAsSeenModalOptions } from '@/components/mark-as-seen/MarkAsSeen';

import ToastHelper, { addTitleToListToast, removeTitleFromListToast } from '@/helpers/toast/toast-helper';
import { SnowplowContext, SnowplowTitleContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { DEFAULT_TITLE_LIST_NAME, TitleList, TitleListName, TitleListTitle } from '@/interfaces/responses/title-list';
import TitleHelper from '@/helpers/title-helper';
import { SeenSeason, WatchListHelper } from '@/helpers/watchlist-helper';

import {
	Episode,
	NewTitle,
	SeenDetailTitle,
	SeenEpisodeDetail,
	SeenSeasonDetail,
	Title,
	TitleObjectType,
	TitleResponse,
} from '@/interfaces/titles';
import { getVm } from '@/helpers/vm-helper';
import { CollectionType } from '@/enums/collection-type';
import { TabType } from '@/interfaces/inner-tabs';
import { ObjectHelper } from '@/helpers/object-helper';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';

export interface ToggleTitleInListPayload {
	list: TitleListName;
	titleInfo: {
		title: Title;
		source: string;
		contexts?: SnowplowContext[];
	};
	showMarkAsSeenModal?: boolean;
}

export interface TitleListStoreStateItem {
	[key: string]: boolean | TitleListTitle;
}

interface SeenListPayload {
	title: string;
	id: number;
	object_type: string;
	show_id?: number;
	season_number?: number;
	episode_number?: number;
	status: boolean;
}

const TOAST_DURATION: number = 3000;

const state = () => ({
	lastListsUpdate: Infinity,
	lastListAction: '', // Make sure that TabType & TitleListName enums have the same values at all times
	// one hash map for each title list type
	[TitleListName.WATCHLIST]: {} as TitleListStoreStateItem,
	[TitleListName.SEENLIST]: {} as TitleListStoreStateItem,
	[TitleListName.LIKELIST]: {} as TitleListStoreStateItem,
	[TitleListName.DISLIKELIST]: {} as TitleListStoreStateItem,
	[TitleListName.MORELIST]: {} as TitleListStoreStateItem,
});

type State = ReturnType<typeof state>;

// used to create the key for a object which should be unique though all
// title‘s that are possible
function createUniqueKeyFromItem(objectType: TitleObjectType, id: number) {
	return `${objectType}_${id}`;
}

function pushErrorToast() {
	const message = getVm().$t('WEBAPP_TITLE_LIST_ISSUE');
	ToastHelper.error({ message, duration: 4000 });
}

function filterBySeen(entity: SeenSeasonDetail | SeenEpisodeDetail) {
	return entity.seen;
}

function formatSeenDetailsTitle(title: NewTitle): {
	objectType: TitleObjectType;
	objectId: number;
	season_number?: number;
	episode_number?: number;
} {
	// I have chosen to keep this way because of readability
	const seenDetailsTitle: Record<string, any> = {
		[TitleObjectType.SEASON]: {
			object_type: TitleObjectType.SHOW,
			id: title.show_id,
			season_number: title.season_number,
		},
		[TitleObjectType.SHOW]: {
			object_type: title.object_type,
			id: title.id,
		},
		[TitleObjectType.MOVIE]: {
			object_type: title.object_type,
			id: title.id,
		},
		[TitleObjectType.EPISODE]: {
			object_type: TitleObjectType.SHOW,
			id: title.show_id,
			season_number: title.season_number,
			episode_number: title.episode_number,
		},
	};

	return seenDetailsTitle[title.object_type];
}

// GETTERS
const getters: GetterTree<State, any> = {
	/**+
	 * returns the information if the given title is in the specific list
	 * TODO Should be renamed to findInList, as `is` means boolean, which is not the case here.
	 */
	isInList:
		state =>
		(titleListName: TitleListName, objectType: TitleObjectType, id: number): TitleListTitle | undefined => {
			if (!(titleListName in state)) return;
			const key = createUniqueKeyFromItem(objectType, id);
			return state[titleListName][key] as TitleListTitle | undefined;
		},

	// TODO: DELETE AFTER FULL GRAPHQL MIGRATION
	/**
	 * returns the information if the given title has been seen
	 * for movies it's the same as for isInList ^
	 * for episodes it's not so straight forward
	 * If needed, the seenDetailsCheck could be used so the seen_details object will be checked for Shows and Seasons
	 * If /title_list/search is able to return episodes with 'jw_entity_id', remove this code with a hashmap by 'jw_entity_id'
	 */
	isTitleSeen:
		(state, getters) =>
		(title: NewTitle, seenDetailsCheck: boolean): boolean => {
			const titleListName = TitleListName.SEENLIST;
			const { object_type: objectType, id } = title;
			if (objectType === TitleObjectType.EPISODE) {
				const show = getters.isInList(titleListName, TitleObjectType.SHOW, title.show_id);
				if (!show) return false;
				const { seen_seasons: seenSeasons = [] }: { seen_seasons: SeenSeason[] } = show;
				const { season_number, episode_number } = title as unknown as Episode;
				const currentSeason = seenSeasons.find(season => season.season_number === season_number);
				if (!currentSeason) return false;
				return currentSeason.seen_episode_numbers.includes(episode_number);
			}
			if (seenDetailsCheck) {
				if (objectType === TitleObjectType.SHOW || objectType === TitleObjectType.MOVIE) {
					// Seen button for TV Show/Movies in Details Page
					const title = getters.isInList(titleListName, objectType, id);
					if (!title?.seen_details) return false;
					return title.seen_details.seen;
				}

				if (objectType === TitleObjectType.SEASON) {
					// the details are in the show object
					const show: TitleListTitle | undefined = getters.isInList(
						titleListName,
						TitleObjectType.SHOW,
						title.show_id
					);

					if (!show || show?.seen_details == null || !Array.isArray(show.seen_details.seasons)) return false;

					const index = show.seen_details.seasons.findIndex(
						(season: SeenSeasonDetail) => title.season_number === season.season_number
					);

					if (index === -1) return false;

					return show.seen_details.seasons[index].seen;
				}
			}

			return getters.isInList(titleListName, objectType, id);
		},

	seenDetailsItem:
		state =>
		(objectType: TitleObjectType, objectId: number): SeenDetailTitle | false => {
			const key = createUniqueKeyFromItem(objectType, objectId);
			const titleState = state[TitleListName.SEENLIST][key];
			if (typeof titleState !== 'boolean') {
				return titleState?.seen_details || false;
			}
			return false;
		},

	seenDetailsMap: (state, getters) => () => {
		const hashmap = getters.listItems(TitleListName.SEENLIST);

		const map: { [key: string]: SeenDetailTitle } = {};
		for (const key in hashmap) {
			const value = hashmap[key];
			if (value.seen_details) {
				map[key] = value.seen_details;
			}
		}
		return map;
	},
};

// ACTIONS
const actions: ActionTree<State, any> = {
	// TODO: All these actions can be deleted once we fully move to GraphQL
	add: async (
		{ rootGetters, getters, rootState, commit, dispatch },
		payload: {
			titleListName: TitleListName;
			objectType: TitleObjectType;
			objectId: number;
			title: string;
			source: string;
			showToast: boolean;
			additionalContexts?: SnowplowContext[];
			listEntryCreatedAt?: string;
			backdrop?: string;
			poster?: string;
			trackingEnabled?: boolean;
		}
	) => {
		// Remove title from opposite list (like/dislike)
		// Example: if title in dislike list and user liked title - we need to remove title from dislike list first
		if ([TitleListName.LIKELIST, TitleListName.DISLIKELIST].includes(payload.titleListName)) {
			const oppositeListName =
				payload.titleListName === TitleListName.LIKELIST ? TitleListName.DISLIKELIST : TitleListName.LIKELIST;
			const isInOppositeList = getters.isInList(oppositeListName, payload.objectType, payload.objectId);

			isInOppositeList &&
				dispatch('remove', {
					...payload,
					titleListName:
						payload.titleListName === TitleListName.LIKELIST
							? TitleListName.DISLIKELIST
							: TitleListName.LIKELIST,
				});
		}

		try {
			const locale = rootGetters['language/locale'];
			// Undo Action needs to restore created date of the list entry
			const { listEntryCreatedAt: created_at } = payload;
			const body = {
				created_at,
			};

			await WatchListHelper.add(
				rootState.user.jwId,
				rootState.user.accessToken,
				payload.titleListName,
				payload.objectType,
				payload.objectId,
				locale,
				body
			);

			commit('LIST_ITEM_ADD', payload);

			const { showToast = true, trackingEnabled = true } = payload;

			if (showToast) {
				const { titleListName, title } = payload;
				const undoCallback = () => dispatch('remove', { ...payload, showToast: false });

				const isTvShow = [TitleObjectType.EPISODE, TitleObjectType.SEASON, TitleObjectType.SHOW].includes(
					payload.objectType
				);
				const isWatchList = payload.titleListName === DEFAULT_TITLE_LIST_NAME;
				if (isTvShow && isWatchList) {
					const vm: Vue = getVm();
					const trAddMessage = vm.$t('WEBAPP_TVSHOWTRACKING_TOAST_ADDED') as string;
					const trRemoveMessage = vm.$t('WEBAPP_TVSHOWTRACKING_TOAST_REMOVED') as string;
					ToastHelper.successWithUndo({
						message: `${title} ${trAddMessage}`,
						undoCallback: {
							message: `${title} ${trRemoveMessage}`,
							callback: undoCallback,
						},
					});
				} else {
					addTitleToListToast(titleListName, title, undoCallback);
				}
			}

			const contexts = (payload.additionalContexts || []).concat([
				new SnowplowTitleContext(payload.objectId, payload.objectType),
			]);
			trackingEnabled &&
				TrackingHelper.trackEvent(`${payload.titleListName}_add`, { action: payload.source }, contexts);
		} catch (err) {
			console.error('[title-list.store]', err);
			pushErrorToast();
		}

		await dispatch('setLastListsUpdate');

		if (TitleListName.SEENLIST === payload.titleListName) {
			// payload.titleListName = TitleListName.WATCHLIST;
			dispatch('title/loadTitles', { collectionType: TitleListName.WATCHLIST }, { root: true });
		}
	},

	remove: async (
		{ rootGetters, rootState, commit, dispatch },
		payload: {
			titleListName: TitleListName;
			objectType: TitleObjectType;
			objectId: number;
			title: string;
			source: string;
			showToast: boolean;
			label?: string;
			additionalContexts?: SnowplowContext[];
			paginationToken?: string;
			trackingEnabled?: boolean;
		}
	) => {
		const {
			additionalContexts,
			objectId,
			objectType,
			paginationToken,
			source,
			showToast = true,
			label,
			title,
			titleListName,
			trackingEnabled = true,
		} = payload;
		try {
			const locale = rootGetters['language/locale'];

			await WatchListHelper.remove(
				rootState.user.jwId,
				rootState.user.accessToken,
				titleListName,
				objectType,
				objectId,
				locale
			);

			commit('LIST_ITEM_REMOVE', payload);

			// Remove item from the list only if in correct list depending on Experiment yes/no
			let removeFromList;
			const filters =
				titleListName === TitleListName.WATCHLIST
					? rootGetters[`filter/${CollectionType.WATCHLIST}`]
					: rootGetters['filter/currentFilters'];
			const { inner_tab } = filters;
			removeFromList = inner_tab === titleListName;

			if (removeFromList) {
				await dispatch(
					'title/removeFromList',
					{ paginationTokens: [paginationToken || objectId] },
					{ root: true }
				);
			}

			if (showToast) {
				const isTvShow = [TitleObjectType.EPISODE, TitleObjectType.SEASON, TitleObjectType.SHOW].includes(
					objectType
				);

				const isWatchList = titleListName === TitleListName.WATCHLIST;
				if (isTvShow && isWatchList) {
					const vm: Vue = getVm();
					const trRemoveMessage = vm.$t('WEBAPP_TVSHOWTRACKING_TOAST_REMOVED') as string;
					ToastHelper.success({ message: `${title} ${trRemoveMessage}` });
				} else {
					removeTitleFromListToast(titleListName, title);
				}
			}

			const properties: TrackingProviderPropertiesInterface = { action: source };
			if (label) {
				properties.label = label;
			}

			const contexts = (additionalContexts || []).concat([new SnowplowTitleContext(objectId, objectType)]);
			trackingEnabled && TrackingHelper.trackEvent(`${titleListName}_remove`, properties, contexts);
		} catch (err) {
			pushErrorToast();
		}

		await dispatch('setLastListsUpdate');
	},
	toggle: (
		{ getters, dispatch, commit },
		payload: {
			titleListName: TitleListName;
			objectType: TitleObjectType;
			objectId: number;
			title: string;
			source: string;
			additionalContexts?: SnowplowContext[];
			paginationToken?: string;
			backdrop?: string;
			poster?: string;
			trackingEnabled?: boolean;
		}
	) => {
		commit('SET_LAST_LIST_ACTION', payload.titleListName);
		const isTitleInList = getters.isInList(payload.titleListName, payload.objectType, payload.objectId);

		if (isTitleInList) {
			return dispatch('remove', payload);
		} else {
			return dispatch('add', payload);
		}
	},

	loadLists: async ({ state, commit, getters, dispatch, rootState }) => {
		const jwId = rootState.user.jwId;
		const accessToken = rootState.user.accessToken;

		if (!jwId) {
			return;
		}

		const seenDetailsMap = getters.seenDetailsMap();

		try {
			const response = await WatchListHelper.fetch(jwId, accessToken);
			const titleLists = response.filter((watchlist: TitleList) => watchlist.titles);
			if (titleLists.length > 0) {
				titleLists.forEach(watchlist => {
					// create initial hash map from watchlist items
					const hashmap = watchlist.titles.reduce(
						(acc: TitleListStoreStateItem, cur: TitleListTitle): TitleListStoreStateItem => {
							const key = createUniqueKeyFromItem(cur.object_type, cur.object_id);
							acc[key] = watchlist.name.toLowerCase() === TitleListName.SEENLIST ? cur : true;
							if (seenDetailsMap[key] && watchlist.name.toLowerCase() === TitleListName.SEENLIST) {
								//@ts-ignore
								acc[key]['seen_details'] = seenDetailsMap[key];
							}
							return acc;
						},
						{}
					);
					commit('LIST_SET', { titleListName: watchlist.name.toLowerCase(), hashmap });
				});
			} else {
				// when there are no items in any list or no lists at all they must
				// be set to empty object here
				const titleListNames = Object.values(TitleListName);
				titleListNames.forEach(titleListName => commit('LIST_RESET', { titleListName }));
			}
		} catch (err) {
			console.error('[title-list.store] loadLists', err);
			if (process.client) {
				pushErrorToast();
			}
		} finally {
			// specials do require initial title lists
			dispatch('specials/setTitleLists', state, { root: true });
		}

		await dispatch('setLastListsUpdate');
	},

	addToSeenList: async ({ commit, rootState, rootGetters }, payload: NewTitle) => {
		const locale = rootGetters['language/locale'];

		// PUT
		await WatchListHelper.add(
			rootState.user.jwId,
			rootState.user.accessToken,
			TitleListName.SEENLIST,
			payload.object_type,
			payload.id,
			locale
		);

		commit('SET_LAST_LISTS_UPDATE', Date.now());
	},

	removeFromSeenList: async ({ commit, rootState, rootGetters }, payload: NewTitle) => {
		const locale = rootGetters['language/locale'];

		await WatchListHelper.remove(
			rootState.user.jwId,
			rootState.user.accessToken,
			TitleListName.SEENLIST,
			payload.object_type,
			payload.id,
			locale
		);

		commit('SET_LAST_LISTS_UPDATE', Date.now());
	},

	fetchItemSeenList: async ({ rootState, rootGetters }, payload: NewTitle) => {
		const locale = rootGetters['language/locale'];

		return WatchListHelper.fetchItem(
			rootState.user.jwId,
			rootState.user.accessToken,
			TitleListName.SEENLIST,
			payload.object_type,
			payload.id,
			locale
		);
	},

	toggleTitleInSeenList: async (
		{ dispatch, getters, state, commit, rootGetters },
		payload: {
			title: NewTitle;
			showTitle?: TitleResponse;
			seenDetailsCheck: boolean;
			source?: string;
			additionalContexts?: SnowplowContext[];
			showToast: boolean;
			trackingEnabled: boolean;
			forceSeenState: boolean | null;
		}
	) => {
		const {
			title,
			showTitle,
			showToast = true,
			seenDetailsCheck = false,
			trackingEnabled = true,
			forceSeenState = null,
		} = payload;

		const trUnseenMessage = getVm().$t('WEBAPP_SEENLIST_TOAST_UNSEEN');
		const trSeenMessage = getVm().$t('WEBAPP_SEENLIST_TOAST_SEEN');
		let messageTitle;

		switch (title.object_type) {
			case TitleObjectType.MOVIE:
			case TitleObjectType.SHOW:
				messageTitle = title.title;
				break;
			case TitleObjectType.SEASON:
				messageTitle = `${title.show_title} S${title.season_number}`;
				break;
			case TitleObjectType.EPISODE:
				messageTitle = `${title.show_title} S${title.season_number} E${title.episode_number}`;
				break;
		}

		const markedAsSeenText = `${messageTitle} ${trSeenMessage}`;
		const unmarkedAsSeenText = `${messageTitle} ${trUnseenMessage}`;
		const isSeen = forceSeenState !== null ? forceSeenState : getters.isTitleSeen(title, seenDetailsCheck);
		const paginationToken = title.pagination_token || showTitle?.pagination_token;
		const seenTab = rootGetters['filter/watchlist'].inner_tab === TabType.COMPLETED_SEEN;
		const titleId = showTitle?.id || title.id; //we want to prioritize the show title over the season title

		commit('SET_LAST_LIST_ACTION', TitleListName.SEENLIST);
		if (isSeen) {
			await dispatch('removeFromSeenList', title);
			seenTab &&
				(await dispatch(
					'title/removeFromList',
					{
						paginationTokens: [paginationToken ? paginationToken : title.id],
					},
					{ root: true }
				));
			await dispatch('changeSeenDetailsStatus', { title, status: !isSeen });
			await dispatch('loadLists');

			commit('LIST_ITEM_REMOVE', {
				titleListName: TitleListName.SEENLIST,
				objectType: title.object_type,
				objectId: title.id || title?.show_id,
			});

			if (showToast) {
				ToastHelper.success({ message: unmarkedAsSeenText, duration: TOAST_DURATION });
			}

			// This is temporary. This will be removed when we refactor this method and use the remove/add methods
			if (payload.source && payload.additionalContexts) {
				const context = title.season_number
					? new SnowplowTitleContext(titleId, title.object_type, title.season_number)
					: new SnowplowTitleContext(titleId, title.object_type);
				const contexts = (payload.additionalContexts || []).concat([context]);

				trackingEnabled &&
					TrackingHelper.trackEvent(`${TitleListName.SEENLIST}_remove`, { action: payload.source }, contexts);
			}
		} else {
			try {
				await dispatch('addToSeenList', title);
				if (seenDetailsCheck) {
					await dispatch('fetchSeenData', formatSeenDetailsTitle(title));
				}

				if (paginationToken) {
					await dispatch(
						'title/refreshWatchList',
						{
							paginationTokens: [paginationToken],
						},
						{ root: true }
					);
				}
				await dispatch('changeSeenDetailsStatus', { title, status: !isSeen });
				await dispatch('loadLists');

				// for tv shows, send the showTitle as this is what is used for like/dislike
				const actualTitle = showTitle ? showTitle : title;
				const isMovieOrTVShow =
					title.object_type === TitleObjectType.MOVIE || title.object_type === TitleObjectType.SHOW;
				const isLiked = getters.isInList(TitleListName.LIKELIST, actualTitle.object_type, actualTitle.id);
				const isDisliked = getters.isInList(TitleListName.DISLIKELIST, actualTitle.object_type, actualTitle.id);

				if (isLiked || isDisliked || !isMovieOrTVShow) {
					if (showToast) {
						ToastHelper.success({ message: markedAsSeenText, duration: TOAST_DURATION });
					}
				} else {
					ModalHelper.openModal(
						LikeDislikePollModal,
						{ closable: true, modalTitle: markedAsSeenText, title: actualTitle },
						{ cssClass: 'jw-poll-modal', id: 'like_dislike_poll_modal' }
					);
				}

				// This is temporary. This will be removed when we refactor this method and use the remove/add methods
				if (payload.source && payload.additionalContexts) {
					const context = title.season_number
						? new SnowplowTitleContext(titleId, title.object_type, title.season_number)
						: new SnowplowTitleContext(titleId, title.object_type);
					const contexts = (payload.additionalContexts || []).concat([context]);
					trackingEnabled &&
						TrackingHelper.trackEvent(
							`${TitleListName.SEENLIST}_add`,
							{ action: payload.source },
							contexts
						);
				}
			} catch (e) {
				console.error('[title-list.store] toggleTitleInSeenList', e);
				ToastHelper.error({ message: getVm().$t('WEBAPP_SEENLIST_TOAST_ERROR'), duration: TOAST_DURATION });
			}
		}

		return;
	},

	toggleTitleInList: async (
		{ dispatch },
		{ list, titleInfo, showMarkAsSeenModal = true }: ToggleTitleInListPayload
	) => {
		if (list === TitleListName.WATCHLIST) {
			const payload = TitleHelper.getWatchlistPayload(titleInfo);
			await dispatch('toggle', payload);

			// Enter mark as seen flow
			if (showMarkAsSeenModal && titleInfo.title.object_type === TitleObjectType.SHOW) {
				const MarkAsSeenModal = () => import('@/components/mark-as-seen/MarkAsSeenModalContainer.vue');

				const { objectType: object_type, objectId: id, title, backdrop, poster } = payload;
				payload.titleListName === TitleListName.WATCHLIST &&
					ModalHelper.openModal(
						MarkAsSeenModal,
						{ title: { id, object_type, title, backdrop, poster } },
						MarkAsSeenModalOptions
					);
			}
		} else if (list === TitleListName.LIKELIST) {
			await dispatch('toggle', TitleHelper.getLikelistPayload(titleInfo));
		} else if (list === TitleListName.DISLIKELIST) {
			await dispatch('toggle', TitleHelper.getDislikelistPayload(titleInfo));
		}
	},

	fetchSeenData: async ({ dispatch, commit, state }, title: { object_type: TitleObjectType; id: number }) => {
		const data = await dispatch('fetchItemSeenList', title);
		const key = createUniqueKeyFromItem(title.object_type, title.id);
		// If the object doesn't exist, it creates it to hold the seen details
		if (!state[TitleListName.SEENLIST][key]) {
			commit('LIST_ITEM_ADD', {
				titleListName: TitleListName.SEENLIST,
				objectType: title.object_type,
				objectId: title.id,
			});
		}
		commit('SET_SEEN_DETAILS_ITEM', {
			title: { object_type: title.object_type, id: title.id },
			payload: data,
		});
	},

	calculateSeenStatusDetails: ({}, entity: SeenDetailTitle) => {
		entity.seasons = entity.seasons.map((season: SeenSeasonDetail) => ({
			...season,
			seen: season.seen_count === season.released_count,
		}));
		entity.seen_count = entity.seasons.filter(filterBySeen).length;
		entity.seen = entity.seen_count === entity.released_count;
		return entity;
	},

	updateSeenDetailsStatus: async (
		{ getters, dispatch },
		{
			object_type,
			id,
			season_number,
			episode_number,
			status,
		}: {
			object_type: TitleObjectType;
			id: number;
			season_number?: number;
			episode_number?: number;
			status: boolean;
		}
	) => {
		const entityToClone: SeenDetailTitle | false = getters.seenDetailsItem(object_type, id);
		if (!entityToClone) {
			return;
		}
		const entity = ObjectHelper.cloneDeep(entityToClone);

		if (object_type === TitleObjectType.MOVIE) {
			entity.seen = status;
			return entity;
		}

		// If object_type === SHOW, apply show status to every episode and season
		if (!season_number && entity.seasons) {
			entity.seasons = entity.seasons.map((season: SeenSeasonDetail) => ({
				...season,
				seen: status,
				episodes: season.episodes.map((episode: SeenEpisodeDetail) => ({ ...episode, seen: status })),
				seen_count: status ? season.released_count : 0,
			}));
			entity.seen_count = entity.seasons.filter(filterBySeen).length;
		}

		// object_type === EPISODE || object_type === SEASON
		if (season_number && Array.isArray(entity.seasons)) {
			const index = entity.seasons.findIndex(
				(season: SeenSeasonDetail) => season_number === season.season_number
			);
			if (index !== -1) {
				const season = entity.seasons[index];
				// objectType === SEASON, apply season status to every episode, apply to all episodes
				if (!episode_number) {
					season.episodes = season.episodes.map((episode: SeenEpisodeDetail) => ({
						...episode,
						seen: status,
					}));
					season.seen = status;
				}

				// objectType === EPISODE
				if (episode_number && Array.isArray(season.episodes)) {
					const episodeIndex = season.episodes.findIndex(
						(episode: SeenEpisodeDetail) => episode.episode_number === episode_number
					);
					const episode = season.episodes[episodeIndex];
					if (episode) {
						episode.seen = status;
					}
				}
				season.seen_count = season.episodes.filter(filterBySeen).length;
			}
		}
		return await dispatch('calculateSeenStatusDetails', entity);
	},

	changeSeenDetailsStatus: async ({ commit, dispatch }, { title, status }: { title: Title; status: boolean }) => {
		const seenDetailsTitle = formatSeenDetailsTitle(title);
		const seenDetailEntity = await dispatch('updateSeenDetailsStatus', {
			...seenDetailsTitle,
			status: status,
		});
		if (seenDetailEntity) {
			commit('SET_SEEN_DETAILS_ITEM', {
				title: seenDetailsTitle,
				payload: seenDetailEntity,
			});
		}
	},

	setLastListsUpdate: ({ commit }) => {
		const newDate = Date.now();
		commit('SET_LAST_LISTS_UPDATE', newDate);
	},
};

// MUTATIONS
const mutations: MutationTree<State> = {
	LIST_SET: (state, payload: { titleListName: TitleListName; hashmap: TitleListStoreStateItem }) => {
		state[payload.titleListName] = payload.hashmap;
	},
	LIST_RESET: (state, payload: { titleListName: TitleListName }) => {
		state[payload.titleListName] = {};
	},
	LIST_ITEM_ADD: (
		state,
		payload: { titleListName: TitleListName; objectType: TitleObjectType; objectId: number }
	) => {
		const key = createUniqueKeyFromItem(payload.objectType, payload.objectId);
		// Since Like/Dislike lists are treated as separate entities, but are two-sides of one coin
		// the check below ensures that we don't put them simultaneously on frontend
		if (payload.titleListName === TitleListName.LIKELIST) {
			Vue.set(state[TitleListName.DISLIKELIST], key, false);
		}
		if (payload.titleListName === TitleListName.DISLIKELIST) {
			Vue.set(state[TitleListName.LIKELIST], key, false);
		}

		Vue.set(state[payload.titleListName], key, payload.titleListName === TitleListName.SEENLIST ? payload : true);
	},
	LIST_ITEM_REMOVE: (
		state,
		payload: { titleListName: TitleListName; objectType: TitleObjectType; objectId: number }
	) => {
		const key = createUniqueKeyFromItem(payload.objectType, payload.objectId);
		Vue.delete(state[payload.titleListName], key);
	},
	SET_LAST_LISTS_UPDATE: (state, value) => (state.lastListsUpdate = value),
	SET_LAST_LIST_ACTION: (state, value) => (state.lastListAction = value),
	SET_SEEN_DETAILS_ITEM: (
		state: any,
		{
			title,
			payload,
		}: {
			title: { object_type: TitleObjectType; id: number };
			payload: SeenListPayload;
		}
	) => {
		const keyItem = createUniqueKeyFromItem(title.object_type, title.id);
		return Vue.set(state[TitleListName.SEENLIST][keyItem], 'seen_details', payload);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<State, any>;
